import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingCart, Package, ClipboardList, ReceiptText, LogOut, KeyRound, PackagePlus, TrendingUp } from 'lucide-react';
import { getStoreToken, getStoreProfile, storeLogout, getCart, cartCount, hydrateCartFromServer } from './storeApi';
import './store.css';

export default function StoreLayout({ children }) {
  const nav = useNavigate();
  const tok = getStoreToken();
  const profile = getStoreProfile();
  // Reactive cart count — listens for storeCartChanged events so the badge
  // updates instantly when any page mutates the cart.
  const [cartQty, setCartQty] = useState(() => cartCount(getCart()));

  useEffect(() => {
    if (!tok) return undefined;
    // Boot-time hydrate from the server so we honour cart persistence after
    // refresh / re-login / multi-device. Show the "restored" toast once.
    let restoreShown = sessionStorage.getItem('store_cart_restore_toast_shown') === '1';
    hydrateCartFromServer().then(({ cart, restored, cleared_reason }) => {
      setCartQty(cartCount(cart));
      if (restored && !restoreShown) {
        toast.success('Your previous cart has been restored.');
        sessionStorage.setItem('store_cart_restore_toast_shown', '1');
      } else if (cleared_reason === 'retention_expired' && !restoreShown) {
        toast.info('Your previous cart expired and has been cleared.');
        sessionStorage.setItem('store_cart_restore_toast_shown', '1');
      }
    }).catch(() => { /* offline — keep cached cart */ });

    const onChange = (e) => setCartQty(cartCount(e?.detail?.cart || getCart()));
    window.addEventListener('storeCartChanged', onChange);
    return () => window.removeEventListener('storeCartChanged', onChange);
  }, [tok]);

  if (!tok) return <Navigate to="/store/login" replace />;
  if (profile && profile.force_password_reset && !window.location.pathname.includes('/change-password')) {
    return <Navigate to="/store/change-password" replace />;
  }

  function handleLogout() {
    sessionStorage.removeItem('store_cart_restore_toast_shown');
    storeLogout();
    nav('/store/login');
  }

  function navLinkClass(args) {
    return 'store-nav-link flex items-center gap-2 ' + (args.isActive ? 'active' : '');
  }
  function mobileNavClass(args) {
    return 'flex flex-col items-center gap-0.5 text-[10px] font-medium ' + (args.isActive ? 'text-orange-600' : 'text-slate-500');
  }

  return (
    <div className="store-portal min-h-screen">
      <header className="store-header shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center font-bold text-white">O</div>
            <div className="leading-tight">
              <p className="text-lg sm:text-xl font-bold tracking-wide text-orange-700">OLTOWNS</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Store Portal</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/store/dashboard" className={navLinkClass}>
              <TrendingUp className="w-4 h-4" /><span>Dashboard</span>
            </NavLink>
            <NavLink to="/store/catalogue" className={navLinkClass}>
              <Package className="w-4 h-4" /><span>Catalogue</span>
            </NavLink>
            <NavLink to="/store/cart" className={navLinkClass}>
              <ShoppingCart className="w-4 h-4" /><span>Cart</span>
              {cartQty > 0 && (
                <span className="ml-1 bg-orange-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{cartQty}</span>
              )}
            </NavLink>
            <NavLink to="/store/orders" className={navLinkClass}>
              <ClipboardList className="w-4 h-4" /><span>My Orders</span>
            </NavLink>
            <NavLink to="/store/invoices" className={navLinkClass}>
              <ReceiptText className="w-4 h-4" /><span>Invoices</span>
            </NavLink>
            <NavLink to="/store/product-requests" className={navLinkClass}>
              <PackagePlus className="w-4 h-4" /><span>Request</span>
            </NavLink>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-xs text-orange-600">{profile && profile.franchise_id}</p>
              <p className="text-sm font-medium text-slate-800 max-w-[140px] truncate">{profile && profile.name}</p>
            </div>
            <button onClick={() => nav('/store/change-password')} title="Change password"
                    className="p-2 rounded-full bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-300" data-testid="store-change-password-btn">
              <KeyRound className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} title="Logout"
                    className="p-2 rounded-full bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-300" data-testid="store-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile bottom nav */}
        <nav className="md:hidden border-t border-orange-200 px-2 py-2 flex items-center justify-around">
          <NavLink to="/store/dashboard" className={mobileNavClass}>
            <TrendingUp className="w-4 h-4" />Stats
          </NavLink>
          <NavLink to="/store/catalogue" className={mobileNavClass}>
            <Package className="w-4 h-4" />Catalogue
          </NavLink>
          <NavLink to="/store/cart" className={mobileNavClass}>
            <ShoppingCart className="w-4 h-4" />Cart
            {cartQty > 0 && (
              <span className="absolute -mt-2 ml-5 bg-orange-500 text-white text-[9px] rounded-full px-1">{cartQty}</span>
            )}
          </NavLink>
          <NavLink to="/store/orders" className={mobileNavClass}>
            <ClipboardList className="w-4 h-4" />Orders
          </NavLink>
          <NavLink to="/store/invoices" className={mobileNavClass}>
            <ReceiptText className="w-4 h-4" />Invoices
          </NavLink>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
